import { ForecastGridRowData } from 'src/models/ForecastModels';
import { ForecastGridFixedFields } from './ForecastGridConstants';

/**
 * Row-reset helpers used by the upload preview's "Reset to system values" context-menu action.
 *
 * The upload-preview grid can flag two classes of edit as errors on an existing row:
 *   1. Non-editable-field changes (segments, budget owner on baseline rows, etc.) — the user
 *      typed a new value into a column they aren't allowed to change on that line item.
 *   2. Baseline-row segment edits — corp / business segment fields are locked on any row
 *      carried over from a previous planning cycle.
 *
 * Both classes have the same fix: snap every field on the row back to whatever the system has
 * stored, and keep ONLY the user's forecast month edits (those are always allowed to change).
 * These helpers do that transformation in one place so the modal handler stays a one-liner.
 */

/** Line-item-id -> system row snapshot. Keyed by string form of `xpt_line_item_id`. */
export type SystemRowLookup = Map<string, ForecastGridRowData>;

/**
 * Build a Map<XptLineItemId, systemRow> from `forecastTemplateCompleteData`. Same key strategy
 * `NonEditableFieldsValidationService` uses, so any row that hit the "field X was modified"
 * error branch is guaranteed to resolve here.
 */
export const buildSystemRowLookup = (completeData: ForecastGridRowData[]): SystemRowLookup => {
  return new Map(
    completeData
      .filter((r) => r[ForecastGridFixedFields.XptLineItemId.value] != null)
      .map((r): [string, ForecastGridRowData] => [`${r[ForecastGridFixedFields.XptLineItemId.value]}`, r])
  );
};

/**
 * Take the user's row (from the uploaded file) and produce a "reset" version: every field
 * overwritten from the system row, except the forecast month values (always user-editable) and
 * the file row number (preserves the row's identity in the preview).
 *
 * Returns `null` when the row can't be reset:
 *   - It has no Line Item Id (brand-new row — nothing in the system to reset TO).
 *   - No matching system row exists for that Line Item Id (defensive: shouldn't happen if the
 *     row is in the errors set, but we no-op instead of throwing).
 *
 * After reset, `is_segment_edited` is cleared — segments once again match the system, so the
 * DMM baseline-row-segment-modified validator won't fire on the next re-validation pass.
 */
export const resetRowToSystemValues = (
  row: ForecastGridRowData | null | undefined,
  systemRowLookup: SystemRowLookup,
  forecastMonthColumnIds: string[]
): ForecastGridRowData | null => {
  if (!row) return null;
  const lineItemId = row[ForecastGridFixedFields.XptLineItemId.value];
  if (lineItemId == null) return null;

  const systemRow = systemRowLookup.get(`${lineItemId}`);
  if (!systemRow) return null;

  // Preserve the user's forecast month edits — the whole point of "reset non-editable fields
  // only" is that the month values they set in Excel are what they actually intended.
  const preservedMonths: Record<string, unknown> = {};
  forecastMonthColumnIds.forEach((col) => {
    preservedMonths[col] = row[col];
  });

  return {
    ...systemRow,
    ...preservedMonths,
    [ForecastGridFixedFields.FileRowNumber.value]: row[ForecastGridFixedFields.FileRowNumber.value],
    [ForecastGridFixedFields.IsSegmentEdited.value]: false
  };
};

/**
 * Fast check for the context-menu enable/disable logic. Cheaper than a full reset since the
 * menu builder runs on every right-click, potentially many times as the user navigates rows.
 */
export const isRowResetEligible = (row: ForecastGridRowData | null | undefined, systemRowLookup: SystemRowLookup): boolean => {
  const lineItemId = row?.[ForecastGridFixedFields.XptLineItemId.value];
  if (lineItemId == null) return false;
  return systemRowLookup.has(`${lineItemId}`);
};
